import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { filter, first, takeWhile, withLatestFrom } from 'rxjs/operators';
import Tabulator from 'tabulator-tables';
import { IDirectReport, IExpenseItem, ITask } from '../../../../lookups';
import { Process } from '../../../../shared';
import { UnsavedChangesService } from '../../../../shared/modal-dialog';
import { LoggingService } from '../../../../shared/services/logging/logging.service';
import { IUserColor } from '../../../../user-info';
import { ExpenseRequestTypeEnum, ExpenseStatus } from '../../../expense-enums';
import { IExpenseSummary } from '../../../IExpenseSummary';
import {
    ActionTypes as ExpenseActionTypes,
    AddExpenseSummarySuccess
} from '../../../store/actions';

@Component({
    selector: 'ef-expense-summary-list',
    templateUrl: './expense-summary-list.component.html',
    styleUrls: ['./expense-summary-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExpenseSummaryListComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    @Input() set expenses(value: Array<IExpenseSummary>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:expenses:set',
            value
        });
        this.ExpensesSubject.next(value.map(x => ({ ...x })));
    }
    @Input() public set approveClick(value: any) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:approveClick',
            value
        });
        if (value) {
            // const summaries = this.tabExpenses.getData() as Array<
            //     IExpenseSummary
            // >;
            // const selected = summaries.filter(x => x.selected);
            const selected = this.tabExpenses.getSelectedData();
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:approveClick',
                selected
            });
            this.onExpenseSummaryApprove(selected);
        }
    }
    @Input() public set validateClick(value: any) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:validateClick',
            value
        });
        if (value) {
            // const summaries = this.tabExpenses.getData() as Array<
            //     IExpenseSummary
            // >;
            // const selected = summaries.filter(x => x.selected);
            const selected = this.tabExpenses.getSelectedData();
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:validateClick',
                selected
            });
            this.onExpenseSummaryValidate(selected);
        }
    }
    @Input() public set unapproveClick(value: any) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:unapproveClick',
            value
        });
        if (value) {
            // const summaries = this.tabExpenses.getData() as Array<
            //     IExpenseSummary
            // >;
            // const selected = summaries.filter(x => x.selected);
            const selected = this.tabExpenses.getSelectedData();
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:unapproveClick',
                selected
            });
            this.onExpenseSummaryUnapprove(selected);
        }
    }
    @Input() public set disapproveClick(value: any) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:disapproveClick',
            value
        });
        if (value) {
            // const summaries = this.tabExpenses.getData() as Array<
            //     IExpenseSummary
            // >;
            // const selected = summaries.filter(x => x.selected);
            const selected = this.tabExpenses.getSelectedData();
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:disapproveClick',
                selected
            });
            this.onExpenseSummaryDisapprove(selected);
        }
    }
    @Input() public set unvalidateClick(value: any) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:unvalidateClick',
            value
        });
        if (value) {
            // const summaries = this.tabExpenses.getData() as Array<
            //     IExpenseSummary
            // >;
            // const selected = summaries.filter(x => x.selected);
            const selected = this.tabExpenses.getSelectedData();
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:unvalidateClick',
                selected
            });
            this.onExpenseSummaryUnvalidate(selected);
        }
    }
    @Input() process: Process;
    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() directReports: Array<IDirectReport>;
    @Input() tasks: Array<ITask>;
    @Input() expenseItems: Array<IExpenseItem>;
    @Input() selectedUser: IDirectReport;
    @Input() superUserEnabled: boolean;
    @Input() userColors: Array<IUserColor>;
    @Input() canOversign: boolean;
    /** Keeps the Summary grid row selected/scrolled to match the active report. */
    @Input() set selectedExpense(value: IExpenseSummary) {
        this.selectedExpReportNbr =
            value?.ExpReportNbr != null && value.ExpReportNbr > 0
                ? value.ExpReportNbr
                : null;
        this.selectAndScrollToExpense(this.selectedExpReportNbr);
    }
    private selectedExpReportNbr: number = null;
    private _showReimbursedAmount: boolean;
    @Input() public set showReimbursedAmount(value: boolean) {
        this._showReimbursedAmount = value;
        if (this.tabExpenses) {
            this.showHideColumns();
        }
    }
    public get showReimbursedAmount(): boolean {
        return this._showReimbursedAmount;
    }
    private _roleId: number;
    @Input() public set roleId(value: number) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:roleId:set',
            value
        });
        this._roleId = value;
        this.RoleSubject.next(value);
    }
    public get roleId(): number {
        return this._roleId;
    }
    _validating: boolean;
    @Input() public set validating(value: boolean) {
        this._validating = value;
        if (value) {
            this.maxStatus = ExpenseStatus.Validated;
        }

        this.showHideColumns();
    }
    public get validating(): boolean {
        return this._validating;
    }
    _approving: boolean;
    @Input() public set approving(value: boolean) {
        this._approving = value;
        if (value) {
            this.maxStatus = ExpenseStatus.Approved;
        }

        this.showHideColumns();
    }
    public get approving(): boolean {
        return this._approving;
    }
    @Output() SelectExpenses = new EventEmitter<IExpenseSummary[]>();
    @Output() clickExpense = new EventEmitter<IExpenseSummary>();
    @Output() editExpense = new EventEmitter<IExpenseSummary>();
    @Output() deleteExpense = new EventEmitter<IExpenseSummary>();
    @Output() oversignExpense = new EventEmitter<IExpenseSummary>();
    @Output() ConvertExpense = new EventEmitter<IExpenseSummary>();
    @Output() ValidateExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() ApproveExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() UnvalidateExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() UnapproveExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() DisapproveExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() AddSelectedExpense = new EventEmitter<number>();
    @Output() RemoveSelectedExpense = new EventEmitter<number>();
    @Output() ClearSelectedExpenses = new EventEmitter();
    @Output() CanClose = new EventEmitter<boolean>();
    @ViewChild('cbxColumnHeader', { static: true })
    cbxColumnHeader: MatCheckbox;
    public loading = true;
    public selectedRowsCheckbox = { checked: false, indeterminate: false };
    public tabExpenses: Tabulator;
    public componentActive = true;
    public RoleSubject = new ReplaySubject<number>(1);
    public ExpensesSubject = new ReplaySubject<Array<IExpenseSummary>>(1);
    public maxStatus: ExpenseStatus = 0;
    public selected: Array<number> = [];
    private placeholderText: string;
    constructor(
        @Inject(UnsavedChangesService)
        public confirmservice: UnsavedChangesService,
        public dialog: MatDialog,
        @Inject(LoggingService) public log: LoggingService,
        public dispatcher: ActionsSubject,
        private ngZone: NgZone
    ) {
        this.log.verbose({ type: 'ExpenseSummaryListComponent::' });
    }
    ngOnInit() {
        this.log.verbose({ type: 'ExpenseSummaryListComponent:ngOnInit' });
    }
    ngAfterViewInit(): void {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:ngAfterViewInit',
            userColors: this.userColors
        });
        if (this.process.ProcessID === 86) {
            // expense approval
            this.placeholderText =
                'No expense reports for the selected person and date range';
        } else {
            this.placeholderText =
                'No expense reports exist for the selected time period';
        }
        this.createTabulator([], []);
        this.showHideColumns();
        this.setupSubscriptions();
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:ngAfterViewInit',
            process: this.process
        });
        this.maxStatus = 0;
    }
    setupSubscriptions() {
        // for any changes in the list of expenses, replace the data in tabulator
        this.ExpensesSubject.pipe(
            takeWhile(() => this.componentActive)
        ).subscribe(expenses => {
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:ExpensesSubject',
                expenses
            });
            this.tabExpenses.replaceData(expenses);
            // replaceData clears row selection — restore selected report + scroll
            setTimeout(() => {
                this.selectAndScrollToExpense(this.selectedExpReportNbr);
            }, 0);
        });
        this.ExpensesSubject.pipe(
            takeWhile(() => this.componentActive),
            filter(x => !!x && x.length > 0),
            first()
        ).subscribe(expenses => {
            this.log.verbose({
                type: 'ExpenseSummaryListComponent:ExpensesSubject',
                expenses
            });
            this.tabExpenses.redraw(true);
        });

        // for new expenses, scroll to the new row
        this.dispatcher
            .pipe(
                ofType<AddExpenseSummarySuccess>(
                    ExpenseActionTypes.AddExpenseSummarySuccess
                ),
                takeWhile(() => this.componentActive),
                withLatestFrom(this.ExpensesSubject)
            )
            .subscribe(([newExpense, expenses]) => {
                this.log.verbose({
                    type: 'ExpenseSummaryListComponent:AddExpenseSummarySuccess',
                    newExpense
                });
                const nbr = newExpense.payload.Items[0].ExpReportNbr;
                if (expenses.find(x => x.ExpReportNbr === nbr)) {
                    this.selectedExpReportNbr = nbr;
                    this.selectAndScrollToExpense(nbr);
                }
            });
    }
    ngOnDestroy() {
        this.log.verbose({ type: 'ExpenseSummaryListComponent:ngOnDestroy' });
        this.componentActive = false;
    }

    /** Select and scroll the Summary row for the given report number. */
    private selectAndScrollToExpense(expReportNbr: number): void {
        if (!this.tabExpenses || expReportNbr == null || expReportNbr <= 0) {
            return;
        }
        try {
            const row = this.tabExpenses.getRow(expReportNbr);
            if (!row) {
                return;
            }
            this.tabExpenses.deselectRow();
            row.select();
            this.tabExpenses.scrollToRow(expReportNbr, 'center', false);
        } catch (err) {
            this.log.warn({
                type: 'ExpenseSummaryListComponent:selectAndScrollToExpense',
                expReportNbr,
                err
            });
        }
    }

    private createTabulator(
        expenses: Array<IExpenseSummary>,
        errors: Array<string>
    ) {
        const comp = this;
        this.log.debug({
            type: 'ExpenseSummaryListComponent:createTabulator',
            expenses
        });
        this.tabExpenses = new Tabulator('#tabExpenses', {
            placeholder: comp.placeholderText,
            maxHeight: '20vh',
            minHeight: '7vh',
            height: '20vh',
            index: 'ExpReportNbr',
            scrollToRowPosition: 'center',
            columnMinWidth: 120,
            selectablePersistence: true,
            selectableCheck: row => {
                const summary = row.getData() as IExpenseSummary;
                return summary.ExpenseReportStatus <= this.maxStatus;
            },
            rowSelectionChanged: (data, rows) => {
                this.log.verbose({
                    type: 'ExpenseSummaryListComponent:tabExpenses:rowSelectionChanged',
                    rows,
                    data
                });
                this.SelectExpenses.emit(data);
            },
            rowFormatter: row => {
                const selected = row.getCell('selected').getElement();
                const summary = row.getData() as IExpenseSummary;
                // comp.log.verbose({
                //     type: 'ExpenseSummaryListComponent:tabulator:rowFormatter',
                //     row,
                //     selected
                // });
                if (comp.maxStatus < summary.ExpenseReportStatus) {
                    selected.classList.add('disabled');
                }
            },
            columns: [
                // 1: selected
                {
                    title: '',
                    field: 'selected',
                    headerSort: false,
                    width: 50,
                    formatter: 'rowSelection',
                    titleFormatter: 'rowSelection',
                    editor: false
                },
                // 2: Report #
                {
                    minWidth: 85,
                    width: 110,
                    title: 'Report #',
                    field: 'ExpReportNbr',
                    visible: true,
                    formatter: c => {
                        const row = c.getRow();
                        const e = c.getElement() as HTMLElement;
                        const exp = row.getData() as IExpenseSummary;
                        // e.classList.add(ExpenseStatus[exp.ExpenseReportStatus]);
                        if (this.userColors && this.userColors.length) {
                            const statusColor = this.userColors.find(
                                color =>
                                    color.StatusID === exp.ExpenseReportStatus
                            );

                            if (statusColor) {
                                try {
                                    let foreColor = statusColor.Forecolor_Hex;
                                    let backColor = statusColor.Backcolor_hex;
                                    let superUserColor;
                                    if (
                                        exp.RequiresOversign &&
                                        this.canOversign
                                    ) {
                                        const pendingOversignColor =
                                            this.userColors.find(
                                                color =>
                                                    color.Status.toLocaleLowerCase() ===
                                                    'pendingoversign'
                                            );
                                        if (pendingOversignColor) {
                                            backColor =
                                                pendingOversignColor.Backcolor_hex;
                                            foreColor =
                                                pendingOversignColor.Forecolor_Hex;
                                        } else {
                                            superUserColor =
                                                this.userColors.find(
                                                    color =>
                                                        color.StatusID ===
                                                        ExpenseStatus.SuperUser
                                                );
                                        }
                                    }

                                    e.style.color = foreColor;
                                    e.style.backgroundColor = backColor;

                                    e.style.borderBottomColor = foreColor;
                                    if (superUserColor) {
                                        e.style.color =
                                            superUserColor.Backcolor_hex;
                                        // e.style.borderColor =
                                        //     superUserColor.Backcolor_hex;
                                        // e.style.borderWidth = '3px';
                                        // e.style.borderStyle = 'solid';
                                        // } else {
                                    }
                                    e.style.borderBottomStyle = 'solid';
                                    e.style.borderBottomWidth = '1px';
                                    if (
                                        exp.ExpenseReportStatus ===
                                        ExpenseStatus.Approved
                                    ) {
                                        e.classList.add('disabled');
                                    }
                                } catch (err) {
                                    this.log.warn(err);
                                }
                            }
                        }
                        return c.getValue();
                    },
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // 3: Report Date
                {
                    minWidth: 100,
                    width: 120,
                    title: 'Report Date',
                    field: 'ReportDate',
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'datetime',
                    formatterParams: c => {
                        return { outputFormat: 'MM/DD/YYYY' };
                    },
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // 4: Report Total
                {
                    minWidth: 102,
                    width: 120,
                    title: 'Report Total',
                    field: 'ReportTotal',
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    },
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // 5
                {
                    minWidth: 90,
                    width: 120,
                    title: 'Allowable',
                    field: 'ReportAllowable',
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    },
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // 6
                {
                    minWidth: 105,
                    width: 120,
                    title: 'Unallowable',
                    field: 'ReportUnallowable',
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    },
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // Reimbursed Amount (GCP + expense reimbursement via payroll)
                {
                    minWidth: 115,
                    width: 130,
                    title: 'Reimbursed Amt',
                    field: 'ReimbursedAmount',
                    visible: false,
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    },
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // 7
                {
                    title: 'Status',
                    field: 'ExpenseReportStatus',
                    // formatter: 'tickCross',
                    // tickElement: '<i class=\'fa fa-check\'></i>',
                    // crossElement: '<i class=\'fa fa-times\'></i>',
                    visible: false,
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                },
                // 8
                {
                    minWidth: 95,
                    width: 150,
                    title: 'Sent to QB',
                    field: 'Exported',
                    cssClass: 'tabulator-cell-readonly',
                    cellDblClick: this.itemDblClick.bind(this),
                    cellClick: this.itemClick.bind(this)
                    // formatter: 'tickCross',
                    // tickElement: '<i class=\'fa fa-check\'></i>',
                    // crossElement: '<i class=\'fa fa-times\'></i>',
                },
                // 9
                {
                    title: '',
                    field: 'Row_Version',
                    visible: false
                },
                {
                    minWidth: 85,
                    width: 120,
                    title: 'Receipt?',
                    field: 'HasReceipt',
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'tickCross',
                    formatterParams: { crossElement: false }
                    // tickElement: '<i class=\'fa fa-check\'></i>',
                    // crossElement: '<i class=\'fa fa-times\'></i>',
                },
                {
                    minWidth: 80,
                    width: 120,
                    title: 'Travel?',
                    field: 'IncludesTravel',
                    cssClass: 'tabulator-cell-readonly',
                    formatter: 'tickCross',
                    formatterParams: { crossElement: false }
                    // tickElement: '<i class=\'fa fa-check\'></i>',
                    // crossElement: '<i class=\'fa fa-times\'></i>',
                },
                // 10
                {
                    minWidth: 60,
                    width: 90,
                    title: 'Oversign',
                    field: 'oversign',
                    visible: false,
                    // Do not gate on roleId/DefaultRoleGroupID: Enter Expense passes the
                    // user's default role, so admins would never see Oversign for their
                    // own reports. Column is already hidden for validate/approve.
                    formatter: c => {
                        const s = c.getData() as IExpenseSummary;
                        if (s.RequiresOversign && this.canOversign) {
                            // span (not <a href>) so click does not navigate away
                            return '<span class="text-link" role="button" tabindex="0">Oversign</span>';
                        }
                        return '';
                    },
                    cellClick: (e, c) => {
                        if (e) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                        const s = c.getData() as IExpenseSummary;
                        comp.log.verbose({
                            type: 'ExpenseSummaryListComponent:Oversign:click',
                            s,
                            canOversign: this.canOversign
                        });
                        if (s.RequiresOversign && this.canOversign) {
                            this.ngZone.run(() =>
                                this.onExpenseSummaryOversign(s)
                            );
                        }
                    },
                    cellTap: (e, c) => {
                        if (e) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                        const s = c.getData() as IExpenseSummary;
                        if (s.RequiresOversign && this.canOversign) {
                            this.ngZone.run(() =>
                                this.onExpenseSummaryOversign(s)
                            );
                        }
                    },
                    headerSort: false
                },
                // 10.1
                {
                    minWidth: 55,
                    width: 80,
                    title: 'Convert',
                    field: 'convert',
                    visible: false,
                    formatter: c => {
                        let result = '';
                        const s = c.getData() as IExpenseSummary;
                        if (
                            s.ExpenseReportStatus === ExpenseStatus.Approved &&
                            s.ERTypeID === ExpenseRequestTypeEnum.Travel
                        ) {
                            result +=
                                '<div class="text-link" (onClick)="onTravelExpenseConvert(s)">Convert</div>';
                        }
                        return result;
                    },
                    cellClick: (e, c) => {
                        const s = c.getData() as IExpenseSummary;
                        this.log.verbose({
                            type: 'ExpenseSummaryListComponent:Convert',
                            s
                        });

                        if (s.ExpenseReportStatus === ExpenseStatus.Approved) {
                            this.onTravelExpenseConvert.bind(this)(s);
                        }
                    },
                    cellTap: (e, c) => {
                        const s = c.getData() as IExpenseSummary;
                        if (s.ExpenseReportStatus === ExpenseStatus.Signed) {
                            this.onTravelExpenseConvert.bind(this)(s);
                        }
                    },
                    headerSort: false
                },
                // 11
                {
                    minWidth: 50,
                    width: 75,
                    title: 'Delete',
                    field: 'delete',
                    visible: false,
                    formatter: c => {
                        const s = c.getData() as IExpenseSummary;
                        if (
                            s.ExpenseReportStatus === ExpenseStatus.Signed ||
                            s.ExpenseReportStatus === ExpenseStatus.Disapproved
                        ) {
                            return `<i class="pi pi-trash ef-button-danger ef-circle-button redText"></i>`;
                        }
                    },
                    cellClick: (e, c) => {
                        const s = c.getData() as IExpenseSummary;
                        if (
                            s.ExpenseReportStatus === ExpenseStatus.Signed ||
                            s.ExpenseReportStatus === ExpenseStatus.Disapproved
                        ) {
                            this.onExpenseSummaryDelete.bind(this)(c.getData());
                        }
                    },
                    cellTap: (e, c) => {
                        const s = c.getData() as IExpenseSummary;
                        if (
                            s.ExpenseReportStatus === ExpenseStatus.Signed ||
                            s.ExpenseReportStatus === ExpenseStatus.Disapproved
                        ) {
                            this.onExpenseSummaryDelete.bind(this)(c.getData());
                        }
                    },
                    headerSort: false
                }
            ],
            layout: 'fitColumns',
            selectable: true,
            initialSort: [{ column: 'ExpReportNbr', dir: 'asc' }],
            data: expenses
        });
    }
    showHideColumns() {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:showHideColumns',
            validating: this.validating,
            approving: this.approving,
            tabExpenses: this.tabExpenses,
            showReimbursedAmount: this.showReimbursedAmount
        });
        if (this.tabExpenses) {
            if (this.showReimbursedAmount) {
                this.tabExpenses.showColumn('ReimbursedAmount');
            } else {
                this.tabExpenses.hideColumn('ReimbursedAmount');
            }
            if (this.validating || this.approving) {
                this.tabExpenses.showColumn('selected');
                this.tabExpenses.hideColumn('oversign');
                this.tabExpenses.hideColumn('convert');
                this.tabExpenses.hideColumn('delete');
            } else {
                this.tabExpenses.hideColumn('selected');
                this.tabExpenses.showColumn('oversign');
                if (this.process.ProcessID === 147) {
                    this.tabExpenses.showColumn('convert');
                }
                this.tabExpenses.showColumn('delete');
                // this.tabExpenses.selectable = 1;
            }
        }
    }
    confirm(emitter: EventEmitter<IExpenseSummary>, summary: IExpenseSummary) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        this.confirmservice
            .confirmPromise({
                dialogText: 'Are you sure you want to delete this expense?'
            })
            .then(() => {
                emitter.emit(summary);
            })
            .catch(() => {});
    }
    private onExpenseSummaryDelete(summary: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryDelete',
            summary
        });
        this.confirm(this.deleteExpense, summary);
    }
    private onExpenseSummaryValidate(summaries: Array<IExpenseSummary>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryValidate',
            summaries
        });
        this.ValidateExpense.emit(summaries);
    }
    private onExpenseSummaryApprove(summaries: Array<IExpenseSummary>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryApprove',
            summaries
        });
        this.ApproveExpense.emit(summaries);
    }
    private onExpenseSummaryUnvalidate(summaries: Array<IExpenseSummary>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryUnvalidate',
            summaries
        });
        this.UnvalidateExpense.emit(summaries);
    }
    private onExpenseSummaryUnapprove(summaries: Array<IExpenseSummary>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryUnapprove',
            summaries
        });
        this.UnapproveExpense.emit(summaries);
    }
    private onExpenseSummaryDisapprove(summaries: Array<IExpenseSummary>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryDisapprove',
            summaries
        });
        this.DisapproveExpense.emit(summaries);
    }
    private onTravelExpenseConvert(summary: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onTravelExpenseConvert',
            summary
        });

        this.ConvertExpense.emit(summary);
        // this.confirmPromise(this.ConvertExpense, summary);
    }
    private onExpenseSummaryOversign(summary: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryOversign',
            summary
        });
        this.oversignExpense.emit(summary);
        // this.confirmPromise(this.oversignExpense, summary);
    }
    private onExpenseSummaryEdit(summary: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummaryEdit',
            summary
        });
        this.editExpense.emit(summary);
    }
    private onExpenseSummarySelect(summary: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:onExpenseSummarySelect',
            summary
        });
        this.clickExpense.emit(summary);
    }
    private itemClick(e, item) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:itemClick',
            item
        });
        item.getTable().deselectRow();
        item.getRow().select();
        // both cells and rows have a getData function
        this.onExpenseSummarySelect(item.getData());
    }
    private itemDblClick(e, item) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:itemDblClick',
            item
        });
        item.getTable().deselectRow();
        item.getRow().select();
        // both cells and rows have a getData function
        this.onExpenseSummaryEdit(item.getData());
    }
}
